import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../../../../services/apiClient';

export const useReference = (referenceType) => {
  const [data, setData] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Функция для обработки ошибок API
  const handleApiError = (result, defaultMessage = 'Сталася помилка') => {
    if (result?.error) {
      const errorCode = result.error.code;
      const errorMessage = result.error.message;
      
      // Обработка конкретных кодов ошибок
      switch (errorCode) {
        case 4141:
          return 'Посада з такою назвою вже існує';
        case 4142:
          return 'Посада не знайдена';
        case 4143:
          return 'Неможливо видалити посаду, оскільки вона використовується';
        default:
          return errorMessage || defaultMessage;
      }
    }
    return defaultMessage;
  };

  // Загрузка конфигурации справочника
  const loadConfig = useCallback(async () => {
    try {
      const result = await apiClient.get(`references/${referenceType}/config`);
      if (result.data?.data) {
        setConfig(result.data.data);
      } else {
        setError(handleApiError(result, 'Помилка завантаження конфігурації'));
      }
    } catch (err) {
      setError('Помилка завантаження конфігурації');
    }
  }, [referenceType]);

  // Загрузка данных справочника
  const loadData = useCallback(async (status = 'active') => {
    setLoading(true);
    setError(null);
    
    try {
      const query = status ? `?status=${status}` : '';
      const result = await apiClient.get(`references/${referenceType}${query}`);
      setLoading(false);

      if (result.data?.data) {
        setData(result.data.data);
      } else {
        setError(handleApiError(result, 'Помилка завантаження даних'));
      }
    } catch (err) {
      setLoading(false);
      setError('Помилка завантаження даних');
    }
  }, [referenceType]);

  // Создание элемента
  const createItem = async (itemData) => {
    setLoading(true);
    setError(null);

    try {
      const url = `references/${referenceType}`;
      const result = await apiClient.post(url, itemData);
      setLoading(false);

      if (result.data) {
        return result.data;
      } else {
        const errorMessage = handleApiError(result, 'Помилка створення');
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      setLoading(false);
      const errorMessage = handleApiError(err, 'Помилка створення');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Обновление элемента
  const updateItem = async (id, itemData) => {
    setLoading(true);
    setError(null);

    try {
      const url = `references/${referenceType}/${id}`;
      const result = await apiClient.put(url, itemData);
      setLoading(false);

      if (result.data) {
        return result.data;
      } else {
        const errorMessage = handleApiError(result, 'Помилка оновлення посади');
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      setLoading(false);
      const errorMessage = handleApiError(err, 'Помилка оновлення посади');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Архивация элемента
  const archiveItem = async (id) => {
    setError(null);

    try {
      const url = `references/${referenceType}/${id}/archive`;
      const result = await apiClient.put(url, {});

      if (result.data) {
        return result.data;
      } else {
        const errorMessage = handleApiError(result, 'Помилка архівації посади');
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      const errorMessage = handleApiError(err, 'Помилка архівації посади');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Восстановление элемента
  const restoreItem = async (id) => {
    setError(null);

    try {
      const url = `references/${referenceType}/${id}/restore`;
      const result = await apiClient.put(url, {});

      if (result.data) {
        return result.data;
      } else {
        const errorMessage = handleApiError(result, 'Помилка відновлення посади');
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      const errorMessage = handleApiError(err, 'Помилка відновлення посади');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Удаление элемента
  const deleteItem = async (id) => {
    setError(null);

    try {
      const url = `references/${referenceType}/${id}`;
      const result = await apiClient.delete(url);

      if (result.data) {
        return result.data;
      } else {
        const errorMessage = handleApiError(result, 'Помилка видалення посади');
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (err) {
      const errorMessage = handleApiError(err, 'Помилка видалення посади');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Очистка ошибки
  const clearError = () => {
    setError(null);
  };

  // Инициализация
  useEffect(() => {
    loadConfig();
    loadData();
  }, [loadConfig, loadData]);

  return {
    data,
    config,
    loading,
    error,
    actions: {
      create: createItem,
      update: updateItem,
      archive: archiveItem,
      restore: restoreItem,
      delete: deleteItem,
      reload: loadData,
      clearError,
    },
  };
};